(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-bandware-native-pdp]';
  const qs = (root, selector) => root?.querySelector(selector) || null;
  const qsa = (root, selector) => Array.from(root?.querySelectorAll(selector) || []);
  const norm = (value) => String(value || '')
    .toLowerCase()
    .replace(/™/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  const same = (a, b) => {
    const left = norm(a);
    const right = norm(b);
    return left === right || left.replace(/\s+/g, '') === right.replace(/\s+/g, '');
  };
  const pathKey = (value) => norm(value).includes('royal') ? 'royal-pop' : 'apple-watch';
  const pathLabel = (value) => value === 'royal-pop' ? 'Royal Pop' : 'Apple Watch';
  const available = (value) => !(value === false || String(value).toLowerCase() === 'false');
  const money = (cents) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
        maximumFractionDigits: 2,
      }).format(Number(cents || 0) / 100);
    } catch (_) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  };

  const presentmentCents = (storeCents) => {
    const rate = Number(window.Shopify?.currency?.rate || 1);
    return Math.round(Number(storeCents || 0) * (Number.isFinite(rate) && rate > 0 ? rate : 1));
  };

  const choice = ({ value, title, copy }) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'bandware-native-pdp__choice';
    button.dataset.value = value;
    button.setAttribute('role', 'radio');
    button.setAttribute('aria-checked', 'false');
    button.innerHTML = `
      <span class="bandware-native-pdp__choice-copy">
        <strong></strong><small></small>
      </span>
      <span class="bandware-native-pdp__choice-state" aria-hidden="true">SELECT</span>`;
    qs(button, 'strong').textContent = title;
    qs(button, 'small').textContent = copy || '';
    return button;
  };

  const deviceCard = ({ value, title, meta, image }) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'bandware-native-pdp__device-card b1-interface-native-selector__choice product-option-card';
    button.dataset.value = value;
    button.setAttribute('role', 'listitem');
    button.setAttribute('aria-pressed', 'false');
    const media = document.createElement('span');
    media.className = 'product-option-card__media';
    if (image) {
      const img = document.createElement('img');
      img.src = image;
      img.alt = '';
      img.loading = 'lazy';
      img.className = 'product-option-card__image';
      media.appendChild(img);
    }
    const label = document.createElement('span');
    label.className = 'product-option-card__label';
    label.textContent = title;
    const metaNode = document.createElement('span');
    metaNode.className = 'product-option-card__meta';
    metaNode.textContent = meta || '';
    button.append(media, label, metaNode);
    return button;
  };

  class BandwareNativePdp {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || '';
      this.data = this.readData();
      this.form = document.getElementById(root.dataset.productFormId || '');
      this.productInfo = root.closest('product-info') || null;
      this.productForm = this.form?.closest('product-form') || null;
      this.submitButton = this.productForm?.querySelector('[type="submit"]') || null;
      this.submitLabel = this.submitButton?.querySelector('span') || null;
      this.baseButtonLabel = this.submitLabel?.textContent.trim() || 'ADD TO CART';
      this.priceRoot = document.getElementById(`price-${this.sectionId}`) || null;
      this.setId = `b1-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
      const initialUrl = new URL(window.location.href);
      this.contextConfirmed = initialUrl.searchParams.has('wear');
      this.contextSource = this.contextConfirmed ? 'url' : 'theme-default';
      this.state = this.initialState();
      this.colorSwatches = this.captureColorSwatches();
      this.captureGallery();
      this.buildOptions();
      this.bind();
      this.apply({ writeUrl: false, source: 'initial' });
      this.bindDeviceRailDiscovery();
    }

    readData() {
      try {
        return JSON.parse(document.getElementById(`bandware-native-data-${this.sectionId}`)?.textContent || '{}');
      } catch (error) {
        console.warn('[BandWare PDP] Invalid pairing data.', error);
        return {};
      }
    }

    supportedPaths() {
      const paths = (this.data.supportedPaths || ['Apple Watch', 'Royal Pop']).map(pathKey);
      return [...new Set(paths.length ? paths : ['apple-watch'])];
    }

    initialState() {
      const url = new URL(window.location.href);
      const paths = this.supportedPaths();
      let wear = pathKey(url.searchParams.get('wear') || this.data.defaultWearPath || paths[0]);
      if (!paths.includes(wear)) wear = paths[0];
      const sizes = this.data.appleSizes || [];
      const rememberedApple = window.BrilintonDeviceContext?.get('appleWatch');
      const requestedSize = url.searchParams.get('size') || rememberedApple?.size || '';
      const apple = requestedSize ? (sizes.find((item) => same(item.size, requestedSize)) || null) : null;
      const models = this.data.royalModels || [];
      const rememberedRoyal = window.BrilintonDeviceContext?.get('royalPop');
      const requestedModel = url.searchParams.get('model') || (rememberedRoyal?.modelSlug || rememberedRoyal?.handle) || '';
      const royal = models.find((item) => same(item.slug || item.name, requestedModel))
        || models.find((item) => same(item.name, 'Ocho Negro'))
        || models[0]
        || null;
      return {
        wear,
        appleSize: apple?.size || '',
        royalModel: royal?.slug || royal?.name || '',
        setMode: this.data.forceCompleteSet === true ? 'complete' : (/^(?:only|bandware-only|bandware)$/i.test(url.searchParams.get('set') || '') ? 'bandware-only' : 'complete'),
      };
    }

    captureColorSwatches() {
      return qsa(document, '[data-bandware-color-swatch]').map((anchor) => {
        const image = qs(anchor, 'img');
        return {
          anchor,
          image,
          baseHref: anchor.dataset.bandwareBaseHref || anchor.getAttribute('href') || '',
          color: anchor.dataset.color || '',
          original: image ? this.captureImageAttributes(image) : null,
        };
      });
    }

    compactImageUrl(source, width = 300) {
      if (!source) return '';
      try {
        const url = new URL(source, window.location.origin);
        url.searchParams.set('width', String(width));
        return url.origin === window.location.origin
          ? `${url.pathname}${url.search}${url.hash}`
          : url.toString();
      } catch (_) {
        return source;
      }
    }

    updateColorSwatches() {
      (this.colorSwatches || []).forEach((swatch) => {
        if (!swatch?.anchor) return;

        const target = new URL(swatch.baseHref || swatch.anchor.getAttribute('href') || '/', window.location.origin);
        target.searchParams.set('wear', this.state.wear);
        target.searchParams.set('set', this.state.setMode === 'complete' ? 'complete' : 'bandware-only');

        if (this.state.wear === 'royal-pop') {
          if (this.state.royalModel) target.searchParams.set('model', this.state.royalModel);
          target.searchParams.delete('size');
        } else {
          if (this.state.appleSize) target.searchParams.set('size', this.state.appleSize);
          target.searchParams.delete('model');
        }

        swatch.anchor.setAttribute('href', `${target.pathname}${target.search}${target.hash}`);

        if (!swatch.image) return;
        this.restoreImage(swatch);

        if (this.state.setMode !== 'complete') return;

        let contextualPreview = null;
        let contextualAlt = '';

        if (this.state.wear === 'royal-pop') {
          // Preserve the existing Royal Pop behavior exactly: the color card previews
          // the matching finished Frame + BandWare + Royal Pop composition.
          const profile = (this.data.frameProfiles || []).find((item) => same(item.finish, swatch.color));
          contextualPreview = (profile?.royalTwelve || []).find((item) => item?.src) || null;
          contextualAlt = `${swatch.color || 'BandWare'} with Royal Pop`;
        } else {
          // Apple Watch now follows the same principle. Each color card previews the
          // matching BandWare Profile contextual gallery instead of its native product image.
          const profile = (this.data.colorProfiles || []).find((item) => same(item.finish, swatch.color));
          const gallery = /49/.test(String(this.state.appleSize || ''))
            ? (profile?.appleUltra || [])
            : (profile?.appleStandard || []);
          contextualPreview = gallery.find((item) => item?.src) || null;
          contextualAlt = `${swatch.color || 'BandWare'} with Apple Watch`;
        }

        if (!contextualPreview?.src) return;

        const previewUrl = this.compactImageUrl(contextualPreview.src, 300);
        swatch.image.setAttribute('src', previewUrl);
        swatch.image.setAttribute('srcset', previewUrl);
        swatch.image.removeAttribute('sizes');
        swatch.image.setAttribute('loading', 'lazy');
        swatch.image.setAttribute('decoding', 'async');
        swatch.image.setAttribute('alt', contextualAlt);
      });
    }

    captureGallery() {
      this.mediaGallery = document.getElementById(`MediaGallery-${this.sectionId}`);
      this.galleryViewer = document.getElementById(`GalleryViewer-${this.sectionId}`);
      this.galleryList = document.getElementById(`Slider-Gallery-${this.sectionId}`);
      this.thumbnailViewer = document.getElementById(`GalleryThumbnails-${this.sectionId}`);
      this.thumbnailList = this.thumbnailViewer?.querySelector('ul') || null;
      this.modalContent = document.querySelector(`#ProductModal-${this.sectionId} .product-media-modal__content`);

      this.gallerySlots = this.captureMediaSlots(
        qsa(this.galleryList, ':scope > [data-bandware-context-slot]'),
        '.product__modal-opener--image img, .product__media img'
      );
      this.thumbnailSlots = this.captureMediaSlots(
        qsa(this.thumbnailList, ':scope > [data-bandware-context-thumbnail]'),
        'img'
      );
      this.modalSlots = this.captureMediaSlots(
        qsa(this.modalContent, ':scope > [data-bandware-context-modal]'),
        null
      );
    }

    captureMediaSlots(nodes, imageSelector) {
      return nodes.map((node) => {
        const image = imageSelector ? node.querySelector(imageSelector) : (node.matches?.('img') ? node : null);
        if (!image) return null;
        return { node, image, original: this.captureImageAttributes(image) };
      }).filter(Boolean);
    }

    captureImageAttributes(image) {
      const attributes = {};
      Array.from(image.attributes || []).forEach((attribute) => {
        attributes[attribute.name] = attribute.value;
      });
      return attributes;
    }

    restoreImage(slot) {
      if (!slot?.image) return;
      Array.from(slot.image.attributes || []).forEach((attribute) => slot.image.removeAttribute(attribute.name));
      Object.entries(slot.original || {}).forEach(([name, value]) => slot.image.setAttribute(name, value));
      slot.image.style.removeProperty('background-image');
      slot.image.style.removeProperty('background-position');
      slot.image.style.removeProperty('background-repeat');
      slot.image.style.removeProperty('background-size');
    }

    projectImage(slot, item, eager = false) {
      if (!slot?.image || !item?.src) return;
      this.restoreImage(slot);
      slot.image.setAttribute('src', item.src);
      slot.image.setAttribute('srcset', item.src);
      slot.image.removeAttribute('sizes');
      slot.image.setAttribute('alt', item.alt || '');
      slot.image.setAttribute('loading', eager ? 'eager' : 'lazy');
      slot.image.setAttribute('decoding', 'async');
      if (item.width) slot.image.setAttribute('width', String(item.width));
      if (item.height) slot.image.setAttribute('height', String(item.height));
    }

    projectCompositeImage(slot, item, eager = false) {
      if (!slot?.image || !item?.src) return;
      if (!item.overlay) {
        this.projectImage(slot, item, eager);
        return;
      }
      this.restoreImage(slot);
      slot.image.setAttribute('src', item.overlay);
      slot.image.setAttribute('srcset', item.overlay);
      slot.image.removeAttribute('sizes');
      slot.image.setAttribute('alt', item.alt || '');
      slot.image.setAttribute('loading', eager ? 'eager' : 'lazy');
      slot.image.setAttribute('decoding', 'async');
      slot.image.style.backgroundImage = `url("${item.src.replace(/"/g, '%22')}")`;
      slot.image.style.backgroundPosition = 'center';
      slot.image.style.backgroundRepeat = 'no-repeat';
      slot.image.style.backgroundSize = 'cover';
      if (item.width) slot.image.setAttribute('width', String(item.width));
      if (item.height) slot.image.setAttribute('height', String(item.height));
    }

    clearOverlay(slot) {
      if (!slot?.node) return;
      slot.node.querySelectorAll(':scope > .bandware-native-pdp__media-overlay').forEach((overlay) => overlay.remove());
      slot.node.classList.remove('bandware-native-pdp__media-active');
    }

    projectOverlay(slot, url, eager = false) {
      this.clearOverlay(slot);
      if (!slot?.node || !url) return;
      slot.node.classList.add('bandware-native-pdp__media-active');
      const overlay = document.createElement('img');
      overlay.className = 'bandware-native-pdp__media-overlay';
      overlay.src = url;
      overlay.alt = '';
      overlay.loading = eager ? 'eager' : 'lazy';
      overlay.decoding = 'async';
      overlay.setAttribute('aria-hidden', 'true');
      slot.node.appendChild(overlay);
    }

    setSlotVisible(slot, visible) {
      if (!slot?.node) return;
      slot.node.hidden = !visible;
      slot.node.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }

    buildOptions() {
      const wearHolder = qs(this.root, '[data-bandware-wear-options]');
      this.supportedPaths().forEach((path) => {
        const isRoyal = path === 'royal-pop';
        const node = choice({
          value: path,
          title: pathLabel(path),
          copy: isRoyal ? 'Pair with the matching B¹ Frame for Royal Pop.' : 'Pair with the correct B¹ Link for your Apple Watch.',
        });
        node.dataset.bandwareWearChoice = path;
        wearHolder?.appendChild(node);
      });

      const appleHolder = qs(this.root, '[data-bandware-apple-options]');
      (this.data.appleSizes || []).forEach((item) => {
        const node = deviceCard({
          value: item.size,
          title: item.size,
          meta: item.series || (item.visualGroup === 'ultra' ? 'Ultra' : 'Standard'),
          image: item.image,
        });
        node.dataset.bandwareAppleChoice = item.size;
        appleHolder?.appendChild(node);
      });

      const royalHolder = qs(this.root, '[data-bandware-royal-options]');
      (this.data.royalModels || []).forEach((item) => {
        const node = deviceCard({ value: item.slug || item.name, title: item.name, meta: item.crownPosition, image: item.image });
        node.dataset.bandwareRoyalChoice = item.slug || item.name;
        royalHolder?.appendChild(node);
      });
    }

    revealSelectedDevice(rail) {
      if (!rail || !window.matchMedia?.('(max-width: 749px)').matches) return;
      const selected = rail.querySelector('.product-option-card.is-selected, .product-option-card[aria-pressed="true"]');
      if (!selected) return;
      requestAnimationFrame(() => {
        const maxLeft = Math.max(0, rail.scrollWidth - rail.clientWidth);
        const idealLeft = selected.offsetLeft - Math.max(0, (rail.clientWidth - selected.offsetWidth) / 2);
        rail.scrollTo({ left: Math.max(0, Math.min(maxLeft, idealLeft)), behavior: 'auto' });
      });
    }

    bindDeviceRailDiscovery() {
      qsa(this.root, '.bandware-native-pdp__branch details').forEach((details) => {
        if (details.dataset.bandwareRailDiscoveryBound === 'true') return;
        const rail = qs(details, '.bandware-native-pdp__device-list');
        if (!rail) return;
        details.dataset.bandwareRailDiscoveryBound = 'true';
        details.addEventListener('toggle', () => {
          if (details.open) this.revealSelectedDevice(rail);
        });
      });
    }

    bind() {
      this.root.addEventListener('click', (event) => {
        const wear = event.target.closest('[data-bandware-wear-choice]');
        if (wear) {
          this.state.wear = wear.dataset.bandwareWearChoice;
          this.state.setMode = 'complete';
          this.contextConfirmed = true;
          this.contextSource = 'wear-selection';
          this.apply({ source: this.contextSource });
          wear.closest('details')?.removeAttribute('open');
          return;
        }
        const size = event.target.closest('[data-bandware-apple-choice]');
        if (size) {
          this.state.wear = 'apple-watch';
          this.state.appleSize = size.dataset.bandwareAppleChoice;
          this.contextConfirmed = true;
          this.contextSource = 'apple-size-selection';
          window.BrilintonDeviceContext?.rememberAppleWatch({ size: this.state.appleSize }, this.contextSource);
          this.apply({ source: this.contextSource });
          size.closest('details')?.removeAttribute('open');
          return;
        }
        const model = event.target.closest('[data-bandware-royal-choice]');
        if (model) {
          this.state.wear = 'royal-pop';
          this.state.royalModel = model.dataset.bandwareRoyalChoice;
          this.contextConfirmed = true;
          this.contextSource = 'royal-model-selection';
          window.BrilintonDeviceContext?.rememberRoyalPop(this.state.royalModel, this.contextSource);
          this.apply({ source: this.contextSource });
          model.closest('details')?.removeAttribute('open');
          return;
        }
        const set = event.target.closest('[data-bandware-set-choice]');
        if (set && !set.disabled && this.data.forceCompleteSet !== true) {
          this.state.setMode = set.dataset.bandwareSetChoice;
          this.apply();
          set.closest('details')?.removeAttribute('open');
        }
      });

      this.productInfo?.addEventListener('brilinton:product-gallery:dom-updated', (event) => {
        if (event.target !== this.productInfo) return;
        // ProductInfo replaces the modal media DOM during a Shopify variant
        // refresh. Re-capture all live slots once, then project the current
        // BandWare context through the central gallery transaction.
        this.captureGallery();
        this.galleryKey = '';
        this.renderNativeGallery();
      });

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        subscribe(PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
          if (String(data?.sectionId || '') !== String(this.sectionId)) return;
          if (data?.variant) {
            this.data.product.variantId = data.variant.id;
            this.data.product.price = data.variant.price;
            this.data.product.available = data.variant.available;
            this.data.product.variantOptions = Array.isArray(data.variant.options)
              ? data.variant.options
              : [data.variant.option1, data.variant.option2, data.variant.option3];
          }
          window.setTimeout(() => {
            const label = this.submitLabel?.textContent.trim();
            if (label && !/\b(?:BOTH|SET)\b/i.test(label) && !/^SOLD OUT$/i.test(label) && !/^SELECT YOUR APPLE WATCH SIZE$/i.test(label)) this.baseButtonLabel = label;
            this.apply({ writeUrl: false, source: 'variant-change' });
          }, 0);
        });
      }
    }

    selectedApple() {
      if (!this.state.appleSize) return null;
      return (this.data.appleSizes || []).find((item) => same(item.size, this.state.appleSize)) || null;
    }

    selectedRoyal() {
      return (this.data.royalModels || []).find((item) => same(item.slug || item.name, this.state.royalModel)) || (this.data.royalModels || [])[0] || null;
    }

    connectionFinish() {
      const index = Number(this.data.product?.connectionFinishOptionIndex);
      const selected = Number.isInteger(index) && index >= 0 ? this.data.product?.variantOptions?.[index] : '';
      return selected && norm(selected) !== 'default title'
        ? selected
        : (this.data.product?.initialConnectionFinish || this.data.product?.color || '');
    }

    linkVariant() {
      const link = this.data.link;
      if (!link?.variants?.length) return null;
      const apple = this.selectedApple();
      const size = apple?.linkSize || '';
      const finish = this.connectionFinish();
      const sizeIndex = Number(link.sizeOptionIndex || 0);
      const finishIndex = Number(link.finishOptionIndex || 0);
      return link.variants.find((variant) =>
        (!size || same(variant.options?.[sizeIndex], size)) && (!finish || same(variant.options?.[finishIndex], finish))
      ) || null;
    }

    frameProfile() {
      const profiles = this.data.frameProfiles || [];
      const finish = this.connectionFinish();
      return profiles.find((profile) => finish && same(profile.finish, finish)) || (profiles.length === 1 ? profiles[0] : null);
    }

    frameVariant() {
      const profile = this.frameProfile();
      return profile?.variant || null;
    }

    rawCompanion() {
      if (this.state.wear === 'royal-pop') {
        const profile = this.frameProfile();
        const variant = this.frameVariant();
        return variant ? { ...variant, kind: 'B¹ Frame', title: profile?.finish ? ('B¹ Frame - ' + profile.finish) : 'B¹ Frame' } : null;
      }
      const variant = this.linkVariant();
      return variant ? { ...variant, kind: 'B¹ Link Pair', title: this.data.link?.title || 'B¹ Link Pair' } : null;
    }

    companion() {
      return this.state.setMode === 'complete' ? this.rawCompanion() : null;
    }

    setName() {
      if (this.state.setMode !== 'complete') return 'BandWare Only';
      return this.state.wear === 'royal-pop' ? 'Frame + BandWare' : 'Link + BandWare';
    }

    enforceAvailableMode() {
      if (this.data.forceCompleteSet === true) {
        this.state.setMode = 'complete';
        return;
      }
      const companion = this.rawCompanion();
      if (this.state.setMode === 'complete' && (!companion || !available(companion.available))) {
        this.state.setMode = 'bandware-only';
      }
    }

    apply({ writeUrl = true, source = this.contextSource || 'bandware-selector' } = {}) {
      const paths = this.supportedPaths();
      if (!paths.includes(this.state.wear)) this.state.wear = paths[0];
      this.enforceAvailableMode();

      qsa(this.root, '[data-bandware-branch]').forEach((node) => { node.hidden = node.dataset.bandwareBranch !== this.state.wear; });
      qsa(this.root, '[data-bandware-wear-choice]').forEach((node) => this.mark(node, node.dataset.bandwareWearChoice === this.state.wear));
      qsa(this.root, '[data-bandware-apple-choice]').forEach((node) => this.mark(node, same(node.dataset.bandwareAppleChoice, this.state.appleSize)));
      qsa(this.root, '[data-bandware-royal-choice]').forEach((node) => this.mark(node, same(node.dataset.bandwareRoyalChoice, this.state.royalModel)));
      qsa(this.root, '[data-bandware-set-choice]').forEach((node) => this.mark(node, node.dataset.bandwareSetChoice === this.state.setMode));

      const wearLabel = qs(this.root, '[data-bandware-wear-label]');
      const appleLabel = qs(this.root, '[data-bandware-apple-label]');
      const royalLabel = qs(this.root, '[data-bandware-royal-label]');
      const setLabel = qs(this.root, '[data-bandware-set-label]');
      if (wearLabel) wearLabel.textContent = pathLabel(this.state.wear).toUpperCase();
      if (appleLabel) appleLabel.textContent = this.selectedApple()?.size || '—';
      if (royalLabel) royalLabel.textContent = this.selectedRoyal()?.name || '—';
      if (setLabel) setLabel.textContent = this.setName().toUpperCase();

      this.updateSetChoices();
      this.updateForm();
      this.updatePrice();
      this.updateSubmit();
      this.updateColorSwatches();
      this.renderNativeGallery();
      if (writeUrl) this.writeUrl();
      document.dispatchEvent(new CustomEvent('bandware:context-change', {
        detail: {
          wear: this.state.wear,
          setMode: this.state.setMode,
          appleSize: this.state.appleSize,
          royalModel: this.state.royalModel,
          connectionFamily: this.state.wear === 'royal-pop' ? 'B¹ Frame' : 'B¹ Link',
          linkSize: this.state.wear === 'apple-watch' ? (this.selectedApple()?.linkSize || '') : '',
          connectionFinish: this.connectionFinish(),
          customerConfirmed: this.contextConfirmed === true,
          source,
        },
      }));
    }

    mark(node, selected) {
      node.classList.toggle('is-selected', selected);
      node.setAttribute('aria-checked', selected ? 'true' : 'false');
      node.setAttribute('aria-pressed', selected ? 'true' : 'false');
      const state = qs(node, '.bandware-native-pdp__choice-state');
      if (state && !node.disabled) state.textContent = selected ? 'SELECTED' : 'SELECT';
    }

    regularSetPrice() {
      const companion = this.rawCompanion();
      return Number(this.data.product?.price || 0) + Number(companion?.price || 0);
    }

    nativeSetDiscount() {
      if (this.data.nativeSetOffer?.enabled !== true || this.state.setMode !== 'complete') return 0;
      const storeDiscount = this.state.wear === 'royal-pop'
        ? Number(this.data.nativeSetOffer?.royalPopDiscount || 0)
        : Number(this.data.nativeSetOffer?.appleWatchDiscount || 0);
      return presentmentCents(storeDiscount);
    }

    nativeSetOfferPrice() {
      return Math.max(0, this.regularSetPrice() - this.nativeSetDiscount());
    }

    nativeSetOfferEnabled() {
      const regular = this.regularSetPrice();
      const discount = this.nativeSetDiscount();
      const offer = this.nativeSetOfferPrice();
      return discount > 0 && offer > 0 && regular > offer;
    }

    updateSetChoices() {
      const complete = qs(this.root, '[data-bandware-set-choice="complete"]');
      const completeTitle = qs(this.root, '[data-bandware-complete-title]');
      const completeCopy = qs(this.root, '[data-bandware-complete-copy]');
      const onlyCopy = qs(this.root, '[data-bandware-only-copy]');
      const companion = this.rawCompanion();
      const valid = Boolean(companion && available(companion.available));
      if (onlyCopy) onlyCopy.textContent = `BandWare™ only · ${money(this.data.product?.price || 0)}`;
      if (completeTitle) {
        completeTitle.textContent = this.state.wear === 'royal-pop'
          ? 'B¹ Frame + BandWare™'
          : 'B¹ Link + BandWare™';
      }
      if (completeCopy) {
        const detail = this.state.wear === 'royal-pop' ? this.connectionFinish() : this.selectedApple()?.size;
        if (!valid) {
          completeCopy.textContent = 'Matching connection unavailable';
        } else {
          completeCopy.textContent = `${detail ? `${detail} · ` : ''}+${money(companion.price || 0)}`;
        }
      }
      if (complete) {
        complete.disabled = !valid;
        complete.setAttribute('aria-disabled', valid ? 'false' : 'true');
        const state = qs(complete, '.bandware-native-pdp__choice-state');
        if (state) state.textContent = valid ? (this.state.setMode === 'complete' ? 'SELECTED' : 'SELECT') : 'UNAVAILABLE';
      }
      const notice = qs(this.root, '[data-bandware-notice]');
      if (notice) {
        notice.hidden = valid;
        notice.classList.toggle('is-error', !valid);
        if (valid) {
          notice.textContent = '';
        } else if (this.data.forceCompleteSet === true) {
          notice.textContent = this.state.wear === 'royal-pop'
            ? 'The matching B¹ Frame is currently unavailable. This Royal Pop configuration is sold out.'
            : 'The matching B¹ Link is currently unavailable. This Apple Watch configuration is sold out.';
        } else {
          notice.textContent = 'The matching connection is unavailable. BandWare™ remains available on its own.';
        }
      }
    }

    updateForm() {
      const companion = this.companion();
      const companionId = qs(this.root, '[data-bandware-companion-id]');
      const companionProperties = qs(this.root, '[data-bandware-companion-properties]');
      if (companionId) companionId.value = companion?.id || '';
      if (companionProperties) companionProperties.value = companion ? JSON.stringify({
        'B¹ Set': this.setName(),
        _b1_set_id: this.setId,
        _b1_set_role: 'connection',
        _b1_set_name: this.setName(),
      }) : '';
      const property = (name, value) => {
        const input = qs(this.root, `[data-bandware-property="${name}"]`);
        if (input) input.value = value || '';
      };
      property('set', this.setName());
      property('wear', pathLabel(this.state.wear));
      property('apple-size', this.state.wear === 'apple-watch' ? this.selectedApple()?.size : '');
      property('royal-model', this.state.wear === 'royal-pop' ? this.selectedRoyal()?.name : '');
      property('connection', companion?.kind || 'Not included');
      property('set-id', this.setId);
      property('connection-finish', this.connectionFinish());
      property('link-size', this.state.wear === 'apple-watch' ? (this.selectedApple()?.linkSize || '') : '');
      property('interface-family', this.state.wear === 'royal-pop' ? 'B¹ Frame' : 'B¹ Link');
      const firstSetImage = this.galleryItems()[0]?.src || '';
      property('cart-image', firstSetImage ? this.compactImageUrl(firstSetImage, 600) : '');
    }

    updatePrice() {
      if (this.data.forceCompleteSet !== true || !this.priceRoot) return;
      const regular = this.regularSetPrice();
      if (!(regular > 0)) return;

      if (this.nativeSetOfferEnabled()) {
        const offer = this.nativeSetOfferPrice();
        this.priceRoot.innerHTML = `
          <div class="price price--large price--on-sale">
            <div class="price__container">
              <div class="price__sale">
                <span class="visually-hidden visually-hidden--inline">Regular price</span>
                <span><s class="price-item price-item--regular">${money(regular)}</s></span>
                <span class="visually-hidden visually-hidden--inline">Sale price</span>
                <span class="price-item price-item--sale price-item--last">${money(offer)}</span>
              </div>
            </div>
          </div>`;
        return;
      }

      this.priceRoot.innerHTML = `
        <div class="price price--large">
          <div class="price__container">
            <div class="price__regular">
              <span class="price-item price-item--regular">${money(regular)}</span>
            </div>
          </div>
        </div>`;
    }

    updateSubmit() {
      if (!this.submitButton || !this.submitLabel) return;
      const needsAppleSize = this.state.wear === 'apple-watch' && !this.selectedApple();
      const companion = this.companion();
      const mainAvailable = available(this.data.product?.available);
      const companionAvailable = this.state.setMode !== 'complete' || (companion && available(companion.available));

      if (needsAppleSize) {
        this.submitButton.disabled = true;
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitLabel.textContent = 'SELECT YOUR APPLE WATCH SIZE';
        return;
      }
      if (!mainAvailable || !companionAvailable) {
        this.submitButton.disabled = true;
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitLabel.textContent = 'SOLD OUT';
        return;
      }
      this.submitButton.disabled = false;
      this.submitButton.removeAttribute('aria-disabled');
      if (companion) {
        const action = /pre-?order/i.test(this.baseButtonLabel) ? 'PRE-ORDER' : 'ADD TO CART';
        if (this.data.forceCompleteSet === true) {
          this.submitLabel.textContent = action;
        } else {
          this.submitLabel.textContent = `${action} — ${money(this.regularSetPrice())}`;
        }
      } else {
        this.submitLabel.textContent = this.baseButtonLabel;
      }
    }

    galleryItems() {
      // Component-only mode always belongs to the product's untouched native media.
      if (this.state.setMode !== 'complete') return [];

      if (this.state.wear === 'apple-watch') {
        const group = this.selectedApple()?.visualGroup === 'ultra' ? 'appleUltra' : 'appleStandard';
        return (this.data.galleries?.[group] || []).filter((item) => item?.src);
      }

      const profile = this.frameProfile();
      if (!profile) return [];
      const model = this.selectedRoyal();
      const crown = norm(model?.crownPosition);
      const useThreeClock = crown.startsWith('3') || crown.includes('3 o');
      const twelve = this.data.galleries?.royalTwelve || [];
      const three = this.data.galleries?.royalThree || [];
      const base = useThreeClock && three.length ? three : twelve;
      const overlays = model?.frameGallery || [];

      return base
        .filter((item) => item?.src)
        .map((item, index) => ({
          ...item,
          overlay: overlays[index] || '',
          alt: `${profile.title || 'B¹ Frame™'} with ${model?.name || 'Royal Pop'}`,
        }));
    }

    renderNativeGallery() {
      if (!this.gallerySlots?.length) return;
      const items = this.galleryItems();
      const key = `${this.state.setMode}|${this.state.wear}|${this.state.appleSize}|${this.state.royalModel}|${items.map((item) => `${item.src}:${item.overlay || ''}`).join('|')}`;
      if (this.galleryKey === key) return;
      this.galleryKey = key;

      const royal = this.state.wear === 'royal-pop';
      const galleryManaged = window.BrilintonProductGallery?.beginContentUpdate?.(this.mediaGallery, {
        reason: 'bandware-context',
      }) === true;

      try {
        this.applyItemsToSlots(this.gallerySlots, items, { overlay: royal });
        this.applyItemsToSlots(this.thumbnailSlots, items, { composite: royal });
        this.applyItemsToSlots(this.modalSlots, items, { composite: royal });
      } finally {
        this.refreshNativeGallery(items.length, galleryManaged);
      }

      document.dispatchEvent(new CustomEvent('bandware:gallery-change', {
        detail: {
          mediaGallery: this.mediaGallery,
          galleryList: this.galleryList,
          contextCount: items.length,
          setMode: this.state.setMode,
          galleryManaged,
        },
      }));
    }

    applyItemsToSlots(slots = [], items = [], { overlay = false, composite = false } = {}) {
      slots.forEach((slot, index) => {
        const item = items[index];
        if (item?.src) {
          this.setSlotVisible(slot, true);
          if (composite) this.projectCompositeImage(slot, item, index === 0);
          else this.projectImage(slot, item, index === 0);
          if (overlay) this.projectOverlay(slot, item.overlay || '', index === 0);
          else this.clearOverlay(slot);
          return;
        }
        this.clearOverlay(slot);
        this.restoreImage(slot);
        this.setSlotVisible(slot, false);
      });
    }

    refreshNativeGallery(contextCount, galleryManaged = false) {
      // Force layout once so the controller sees only the final projected state.
      void this.galleryList?.offsetWidth;

      const firstContext = this.gallerySlots.find((slot) => !slot.node.hidden)?.node || null;
      const firstNative = qs(this.galleryList, ':scope > .product__media-item:not([data-bandware-context-slot]):not([hidden])');
      const firstMediaId = (firstContext || firstNative)?.dataset?.mediaId;
      const visibleTotal = Number(contextCount || 0)
        + qsa(this.galleryList, ':scope > .product__media-item:not([data-bandware-context-slot]):not([hidden])').length;

      if (galleryManaged) {
        window.BrilintonProductGallery.endContentUpdate(this.mediaGallery, {
          reason: 'bandware-context',
          activeMediaId: firstMediaId,
          resetPosition: true,
          contentChanged: true,
        });
        return;
      }

      // Compatibility fallback if the central gallery controller is unavailable.
      this.galleryViewer?.resetPages?.();
      this.thumbnailViewer?.resetPages?.();
      if (firstMediaId) this.mediaGallery?.setActiveMedia?.(firstMediaId, false, { scrollPage: false });
      qsa(this.galleryViewer, '.slider-counter--total').forEach((node) => { node.textContent = String(Math.max(visibleTotal, 1)); });
      const customCounter = qs(this.galleryViewer, '#slideCounter');
      if (customCounter) customCounter.textContent = `1 / ${Math.max(visibleTotal, 1)}`;
    }

    writeUrl() {
      const url = new URL(window.location.href);
      url.searchParams.set('wear', this.state.wear);
      url.searchParams.set('set', this.state.setMode === 'complete' ? 'complete' : 'bandware-only');
      if (this.state.wear === 'apple-watch') {
        if (this.state.appleSize) url.searchParams.set('size', this.state.appleSize);
        url.searchParams.delete('model');
      } else {
        if (this.state.royalModel) url.searchParams.set('model', this.state.royalModel);
        url.searchParams.delete('size');
      }
      history.replaceState(history.state || {}, '', `${url.pathname}${url.search}${url.hash}`);
    }
  }

  const boot = (scope = document) => qsa(scope, ROOT_SELECTOR).forEach((root) => {
    if (!root.__bandwareNativePdp) root.__bandwareNativePdp = new BandwareNativePdp(root);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
